import { useMutation, useQuery } from "@tanstack/react-query";

import { ApiError, api } from "../../api/client";
import type { TerrainAnalysisRequest, TerrainReport } from "../../api/types";
import { t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { useDeployment } from "../units/useDeployment";
import { outdatedRefusal, zoneRefusalOf } from "../zones/zoneRefusal";
import { displayedDraft, inputsKey, staleReport, type TerrainInputs, type TerrainReportWeather, terrainInputs } from "./terrainWeather";

/** Analyse du terrain (registre H) : requête construite depuis le dossier affiché — camp, niveau et mission
 *  (posture), début d'exercice, fuseau et météo (une même source : le brouillon gardé sur ce poste s'il y en a
 *  un, sinon le dossier enregistré, `terrainInputs`), unités au temps affiché (menace, dispositions), ponts
 *  détruits —, résultat gardé dans l'état de l'interface pour la carte et le panneau, avec ses entrées et l'heure
 *  du calcul : un rapport calculé pour une autre météo ou une autre date est dit périmé (`stale`). */

/** Anneau de progression de 2 s avant le rapport : choix produit de l'iPad (rythme perçu du traitement) ; le
 *  pied du rapport donne le temps de calcul réel. */
const MIN_DISPLAY_MS = 2_000;

/** Corps de la requête (hors serveur : fonction pure, essayée seule). La météo part brute (choix et identifiant)
 *  : le serveur compose la clé comme celle des ordres. */
export function analysisRequest(
  polygon: [number, number][] | null,
  axis: boolean,
  context: {
    level: TerrainAnalysisRequest["level"];
    mission: string | null;
    inputs: TerrainInputs;
    units: TerrainAnalysisRequest["units"];
    destroyedBridges: string[];
  },
): TerrainAnalysisRequest & { weather_custom: TerrainInputs["weather_custom"]; destroyed_bridges: string[] } {
  return {
    polygon,
    level: context.level,
    mission: context.mission,
    start_dtg: context.inputs.start_dtg,
    time_zone: context.inputs.time_zone,
    weather_scenario: context.inputs.weather_scenario,
    weather_custom: context.inputs.weather_custom,
    units: context.units,
    destroyed_bridges: context.destroyedBridges,
    axis,
  };
}

export function useTerrainAnalysis() {
  const mapSlug = useUi((s) => s.mapSlug);
  const params = useUi((s) => s.deploymentParams);
  const exerciseId = useUi((s) => s.exerciseId);
  const phase = useUi((s) => s.phase);
  const terrain = useUi((s) => s.terrain);
  const setTerrain = useUi((s) => s.setTerrain);
  const { data: deployment } = useDeployment();
  const { data: exercises } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises?.find((e) => e.id === exerciseId);
  // Météo, date et fuseau du dossier affiché : relus à chaque rendu (le brouillon vit sur ce poste).
  const inputs = terrainInputs(exercise, displayedDraft(exerciseId, exercise));

  const request = (polygon: [number, number][] | null, axis: boolean) =>
    analysisRequest(polygon, axis, {
      level: params?.level ?? exercise?.parameters.level ?? null,
      mission: params?.mission ?? exercise?.parameters.mission ?? null,
      inputs,
      units: (deployment?.units ?? [])
        .filter((u) => !u.hidden && u.positions[phase])
        .map((u) => ({
          name: u.display_name ?? u.designation ?? u.code,
          side: u.side,
          lon: u.positions[phase]!.lon,
          lat: u.positions[phase]!.lat,
          arm: u.arm,
          specialty: u.specialty,
          materiel: u.materiel.map((m) => m.name),
        })),
      destroyedBridges: deployment?.placement?.destroyed_bridges ?? [],
    });

  const analysis = useMutation({
    mutationFn: async ({ polygon, axis, rerun = false }: { polygon: [number, number][] | null; axis: boolean; rerun?: boolean }) => {
      if (!mapSlug) throw new ApiError(422, t("Choisissez d'abord un camp dans les Données initiales."));
      const started = performance.now();
      const sent = inputsKey(inputs);
      // Durée réelle du calcul (réponse du serveur), sans l'attente minimale de l'anneau.
      const pending = api
        .terrainAnalysis(mapSlug, request(polygon, axis))
        .then((report) => ({ report, elapsed: performance.now() - started, at: Date.now() }));
      // Relance d'un rapport périmé : tout le rapport est recalculé, axe compris, avec l'anneau comme une analyse.
      const wait = axis && !rerun ? Promise.resolve() : new Promise((resolve) => window.setTimeout(resolve, MIN_DISPLAY_MS));
      const [{ report, elapsed, at }] = await Promise.all([pending, wait]);
      return { report, elapsed, phase, sent, at };
    },
    onSuccess: ({ report, elapsed, phase: at, sent, at: computedAt }) => {
      const kept: TerrainReportWeather = { ...report, elapsed_ms: Math.round(elapsed), phase: at, inputs: sent, computed_at: computedAt };
      setTerrain(kept as TerrainReport);
    },
  });

  return {
    terrain,
    canAnalyze: Boolean(mapSlug && deployment),
    busy: analysis.isPending,
    /** Calcul en cours : rapport (anneau de progression, relance d'un rapport périmé comprise) ou axe favorable seul
     *  (bouton). */
    busyKind: analysis.isPending ? (analysis.variables?.axis && !analysis.variables.rerun ? "axis" : "analysis") : null,
    /** Rapport calculé pour une autre météo, une autre date ou un autre fuseau que ceux du dossier affiché. */
    stale: terrain ? staleReport(terrain, inputs) : false,
    /** Refus propre à une zone libre pas encore prête (409) : l'interface désigne l'action attendue. */
    refused: zoneRefusalOf(analysis.error) !== null,
    /** Ce refus est-il devenu sans objet (« terrain pas prêt » alors que la zone est prête, `outdatedRefusal`) ? */
    outdatedFor: (pendingZone: unknown) => outdatedRefusal(analysis.error, pendingZone),
    error:
      analysis.error instanceof ApiError
        ? analysis.error.message
        : analysis.error
          ? t("Analyse impossible : le serveur ne répond pas.")
          : null,
    analyzeCamp: () => analysis.mutate({ polygon: null, axis: false }),
    analyzeZone: (ring: [number, number][]) => analysis.mutate({ polygon: ring, axis: false }),
    /** Axe favorable à travers la zone affichée (même zone, rapport recalculé avec l'axe). */
    computeAxis: () => {
      if (!terrain) return;
      analysis.mutate({ polygon: terrain.camp ? null : terrain.polygon, axis: true });
    },
    /** Rapport périmé relancé sur la même zone, l'axe recalculé s'il était affiché. */
    rerun: () => {
      if (!terrain) return;
      analysis.mutate({ polygon: terrain.camp ? null : terrain.polygon, axis: terrain.axis !== undefined, rerun: true });
    },
    clear: () => setTerrain(null),
  };
}
